// Google OAuth is an addition, not a replacement. Both local and Google login methods are available.
import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Container,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { resendVerification, requestPasswordReset } from '../api/auth';

// Импортируем компоненты из UI Kit
import { TextField, Button, useThemeMode } from '../components/ui-kit';
import { API_URL } from '../api/config';

// Theme toggle component
const ThemeToggle = () => {
  const { mode, toggleMode } = useThemeMode();

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        zIndex: 1000,
      }}
    >
      <IconButton onClick={toggleMode} color="inherit">
        {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
    </Box>
  );
};

// Основной контейнер
const AuthContainer = styled(Box)(
  ({ theme, isRightPanelActive, isMobile }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
    borderRadius: '0.7rem',
    boxShadow:
      '0 0.9rem 1.7rem rgba(0, 0, 0, 0.25), 0 0.7rem 0.7rem rgba(0, 0, 0, 0.22)',
    height: isMobile ? 'auto' : '520px',
    maxWidth: '758px',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    margin: 'auto',
    [theme.breakpoints.down('sm')]: {
      boxShadow: isMobile ? 'none' : undefined,
      borderRadius: isMobile ? 0 : undefined,
      height: 'auto',
      minHeight: isMobile ? '100vh' : 'auto',
    },
  })
);

// Контейнер для форм
const FormContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  position: 'absolute',
  top: 0,
  transition: 'all 0.6s ease-in-out',
}));

// Контейнер для формы входа
const SignInContainer = styled(FormContainer)(
  ({ theme, isRightPanelActive, isMobile }) => ({
    left: 0,
    width: isMobile ? '100%' : '50%',
    zIndex: 2,
    position: isMobile ? 'relative' : 'absolute',
    ...(!isMobile &&
      isRightPanelActive && {
        transform: 'translateX(100%)',
      }),
    [theme.breakpoints.down('sm')]: {
      display: isMobile && isRightPanelActive ? 'none' : 'block',
    },
  })
);

// Контейнер для формы регистрации
const SignUpContainer = styled(FormContainer)(
  ({ theme, isRightPanelActive, isMobile }) => ({
    left: 0,
    opacity: isMobile ? 1 : 0,
    width: isMobile ? '100%' : '50%',
    zIndex: 1,
    position: isMobile ? 'relative' : 'absolute',
    ...(!isMobile &&
      isRightPanelActive && {
        animation: 'show 0.6s',
        opacity: 1,
        transform: 'translateX(100%)',
        zIndex: 5,
      }),
    [theme.breakpoints.down('sm')]: {
      display: isMobile && !isRightPanelActive ? 'none' : 'block',
    },
    '@keyframes show': {
      '0%, 49.99%': {
        opacity: 0,
        zIndex: 1,
      },
      '50%, 100%': {
        opacity: 1,
        zIndex: 5,
      },
    },
  })
);

// Контейнер для оверлея
const OverlayContainer = styled(Box)(({ theme, isRightPanelActive }) => ({
  height: '100%',
  left: '50%',
  overflow: 'hidden',
  position: 'absolute',
  top: 0,
  transition: 'transform 0.6s ease-in-out',
  width: '50%',
  zIndex: 100,
  ...(isRightPanelActive && {
    transform: 'translateX(-100%)',
  }),
}));

// Оверлей
const Overlay = styled(Box)(({ theme, isRightPanelActive }) => ({
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(90deg, rgba(10, 130, 180, 1) 0%, rgba(100, 60, 140, 1) 80%)'
      : 'linear-gradient(90deg, rgba(12, 181, 247, 1) 0%, rgba(152, 87, 199, 1) 80%)',
  backgroundAttachment: 'fixed',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  height: '100%',
  left: '-100%',
  position: 'relative',
  transform: 'translateX(0)',
  transition: 'transform 0.6s ease-in-out',
  width: '200%',
  ...(isRightPanelActive && {
    transform: 'translateX(50%)',
  }),
}));

// Панель оверлея
const OverlayPanel = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  position: 'absolute',
  textAlign: 'center',
  top: 0,
  transform: 'translateX(0)',
  transition: 'transform 0.6s ease-in-out',
  width: '50%',
  padding: '0 3rem',
}));

// Левая панель оверлея
const LeftOverlayPanel = styled(OverlayPanel)(
  ({ theme, isRightPanelActive }) => ({
    transform: 'translateX(-20%)',
    ...(isRightPanelActive && {
      transform: 'translateX(0)',
    }),
  })
);

// Правая панель оверлея
const RightOverlayPanel = styled(OverlayPanel)(
  ({ theme, isRightPanelActive }) => ({
    right: 0,
    transform: 'translateX(0)',
    ...(isRightPanelActive && {
      transform: 'translateX(20%)',
    }),
  })
);

// Форма
const FormContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: '0 3rem',
  height: '100%',
  textAlign: 'center',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: '0 2rem 1rem 2rem',
  },
}));

// Контейнер для формы (обеспечивает одинаковую высоту)
const FormInnerContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  justifyContent: 'space-between',
  padding: '30px 0',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: '40px',
  },
}));

// Контейнер для полей формы
const FormFieldsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  justifyContent: 'center',
  marginTop: '20px',
  marginBottom: '20px',
}));

// Стилизованная кнопка для оверлея
const OverlayButton = styled(Button)(({ theme, backgroundColor }) => ({
  backgroundColor: theme.palette[backgroundColor].main,
  backgroundImage: 'none',
  textTransform: 'uppercase',
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Стили для форм
const StyledFormBox = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '16px',
}));

// Validation schemas
const loginValidationSchema = Yup.object({
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const registerValidationSchema = Yup.object({
  fullName: Yup.string().required('Full name is required'),
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  passwordConfirm: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

// Mobile navigation component
const MobileNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '16px',
  marginBottom: '16px',
  width: '100%',
}));

function Auth({ initialActivePanel = 'login' }) {
  const { login, register, isAuthenticating, isAuthenticated } = useAuth();
  const { mode } = useThemeMode();
  const isDarkMode = mode === 'dark';
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterPasswordConfirm, setShowRegisterPasswordConfirm] =
    useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Сессия уже есть (в т.ч. поднята по общей refresh-куке при старте) — форма входа не нужна
  useEffect(() => {
    if (isAuthenticated) {
      navigate(location.state?.from || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  // Check if the screen is mobile size
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // Toggle password visibility handlers
  const handleToggleLoginPassword = () => {
    setShowLoginPassword(!showLoginPassword);
  };

  const handleToggleRegisterPassword = () => {
    setShowRegisterPassword(!showRegisterPassword);
  };

  const handleToggleRegisterPasswordConfirm = () => {
    setShowRegisterPasswordConfirm(!showRegisterPasswordConfirm);
  };

  // Handle forgot password click
  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setForgotPasswordEmail(loginFormik ? loginFormik.values.email : '');
    setForgotPasswordOpen(true);
  };

  const handleSendResetLink = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Enter your email address first');
      return;
    }
    setIsSendingReset(true);
    try {
      const response = await requestPasswordReset(forgotPasswordEmail);
      toast.success(response?.message || 'If this email is registered, a password reset link has been sent.');
      setForgotPasswordOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reset link');
    } finally {
      setIsSendingReset(false);
    }
  };

  // Определяем, активна ли правая панель
  const [isRightPanelActive, setIsRightPanelActive] = useState(
    initialActivePanel === 'register' || location.pathname === '/register'
  );

  // Check for error parameters in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const error = searchParams.get('error');
    
    if (error === 'account_disabled') {
      // Redirect to inactive user page instead of showing toast
      navigate('/inactive-user', { replace: true });
    }
  }, [location.search, navigate]);

  // Обновляем активную форму при изменении URL
  useEffect(() => {
    const path = location.pathname;
    if (path === '/register') {
      setIsRightPanelActive(true);
      setLoginError('');
      setRegisterError('');
    } else if (path === '/login' || path === '/') {
      setIsRightPanelActive(false);
      setLoginError('');
      setRegisterError('');
    }
  }, [location.pathname]);

  const handleSignInClick = () => {
    setIsRightPanelActive(false);
    navigate('/login', { replace: true });
  };

  const handleSignUpClick = () => {
    setIsRightPanelActive(true);
    navigate('/register', { replace: true });
  };

  const loginFormik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      setLoginError('');

      try {
        await login(values.email, values.password);
        // Если успешно залогинились, то редирект произойдет в AuthContext
      } catch (err) {
        console.log('Login error in form:', err.response?.data);

        // Обработка ответа от сервера
        const responseData = err.response?.data;

        // Проверяем, есть ли ошибки валидации полей
        if (
          responseData?.errors &&
          Array.isArray(responseData.errors) &&
          responseData.errors.length > 0
        ) {
          // Если есть ошибки валидации - отображаем их для соответствующих полей
          responseData.errors.forEach((error) => {
            if (error.path && error.message) {
              setFieldError(error.path, error.message);
            }
          });

          // Для ошибок валидации показываем тост с общим сообщением
          if (responseData.error === 'Validation Error') {
            toast.error('Please check the form for errors');
          }
        } else {
          // Check for specific account disabled error
          if (responseData?.code === 'ACCOUNT_DISABLED') {
            // Redirect to inactive user page instead of showing error
            navigate('/inactive-user', { replace: true });
            return;
          }

          // Email not verified: identity + password already proven, only the
          // confirmation link is missing — auto-resend it rather than making
          // the person hunt for a "resend" button.
          if (responseData?.code === 'EMAIL_NOT_VERIFIED') {
            const message = 'Please confirm your email address before signing in. We have sent you a new verification link.';
            setLoginError(message);
            toast.info(message, { autoClose: 8000 });
            resendVerification(responseData.email || values.email).catch(() => {});
            return;
          }

          // Для ошибок аутентификации показываем и алерт, и тост
          const errorMessage =
            responseData?.error ||
            responseData?.message ||
            'An error occurred during login';
          setLoginError(errorMessage);
          toast.error(errorMessage);
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const registerFormik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      password: '',
      passwordConfirm: '',
    },
    validationSchema: registerValidationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      setRegisterError('');

      try {
        const result = await register(
          values.fullName,
          values.email,
          values.password,
          values.passwordConfirm
        );
        if (result) {
          // После успешной регистрации переключаем на форму логина
          setIsRightPanelActive(false);
          navigate('/login', { replace: true });
        } else {
          setRegisterError('Registration failed');
        }
      } catch (err) {
        console.log('Registration error in form:', err.response?.data);

        // Обработка ответа от сервера
        const responseData = err.response?.data;

        // Проверяем, есть ли ошибки валидации полей
        if (
          responseData?.errors &&
          Array.isArray(responseData.errors) &&
          responseData.errors.length > 0
        ) {
          // Если есть ошибки валидации - отображаем их для соответствующих полей
          responseData.errors.forEach((error) => {
            if (error.path && error.message) {
              setFieldError(error.path, error.message);
            }
          });

          // Для ошибок валидации показываем тост с общим сообщением
          if (responseData.error === 'Validation Error') {
            toast.error('Please check the form for errors');
          }
        } else {
          // Для других ошибок показываем и алерт, и тост
          const errorMessage =
            responseData?.error ||
            responseData?.message ||
            'Registration failed';
          setRegisterError(errorMessage);
          toast.error(errorMessage);
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDarkMode
          ? 'linear-gradient(90deg, rgba(10, 130, 180, 1) 0%, rgba(100, 60, 140, 1) 80%)'
          : 'linear-gradient(90deg, rgba(12, 181, 247, 1) 0%, rgba(152, 87, 199, 1) 80%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        py: isMobile ? 0 : 4,
      }}
    >
      <ThemeToggle />
      <Container maxWidth="md" sx={{ p: isMobile ? 0 : 2 }}>
        <AuthContainer
          isRightPanelActive={isRightPanelActive}
          isMobile={isMobile}
        >
          {/* Sign Up Container */}
          <SignUpContainer
            isRightPanelActive={isRightPanelActive}
            isMobile={isMobile}
          >
            <FormContent>
              <FormInnerContent>
                <Box>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{ fontWeight: 300, mb: 2 }}
                    color="text.primary"
                  >
                    Sign Up
                  </Typography>
                </Box>

                <FormFieldsContainer>
                  <StyledFormBox
                    component="form"
                    noValidate
                    onSubmit={registerFormik.handleSubmit}
                    sx={{ width: '100%' }}
                  >
                    <TextField
                      required
                      fullWidth
                      id="fullName"
                      label="Full name"
                      name="fullName"
                      placeholder="Enter your full name"
                      autoComplete="name"
                      value={registerFormik.values.fullName}
                      onChange={registerFormik.handleChange}
                      onBlur={registerFormik.handleBlur}
                      error={
                        registerFormik.touched.fullName &&
                        Boolean(registerFormik.errors.fullName)
                      }
                      helperText={
                        registerFormik.touched.fullName &&
                        registerFormik.errors.fullName
                      }
                    />
                    <TextField
                      required
                      fullWidth
                      id="register-email"
                      label="Email"
                      name="email"
                      placeholder="Enter your email"
                      autoComplete="email"
                      value={registerFormik.values.email}
                      onChange={registerFormik.handleChange}
                      onBlur={registerFormik.handleBlur}
                      error={
                        registerFormik.touched.email &&
                        Boolean(registerFormik.errors.email)
                      }
                      helperText={
                        registerFormik.touched.email &&
                        registerFormik.errors.email
                      }
                    />
                    <TextField
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type={showRegisterPassword ? 'text' : 'password'}
                      id="register-password"
                      placeholder="Enter your password"
                      autoComplete="new-password"
                      value={registerFormik.values.password}
                      onChange={registerFormik.handleChange}
                      onBlur={registerFormik.handleBlur}
                      error={
                        registerFormik.touched.password &&
                        Boolean(registerFormik.errors.password)
                      }
                      helperText={
                        registerFormik.touched.password &&
                        registerFormik.errors.password
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleToggleRegisterPassword}
                              edge="end"
                            >
                              {showRegisterPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      required
                      fullWidth
                      name="passwordConfirm"
                      label="Confirm Password"
                      type={showRegisterPasswordConfirm ? 'text' : 'password'}
                      id="register-password-confirm"
                      placeholder="Confirm your password"
                      autoComplete="new-password"
                      value={registerFormik.values.passwordConfirm}
                      onChange={registerFormik.handleChange}
                      onBlur={registerFormik.handleBlur}
                      error={
                        registerFormik.touched.passwordConfirm &&
                        Boolean(registerFormik.errors.passwordConfirm)
                      }
                      helperText={
                        registerFormik.touched.passwordConfirm &&
                        registerFormik.errors.passwordConfirm
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleToggleRegisterPasswordConfirm}
                              edge="end"
                            >
                              {showRegisterPasswordConfirm ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {registerError && (
                      <Alert
                        severity="error"
                        sx={{
                          mt: 1,
                          mb: 1,
                          width: '100%',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 16px',
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          border: '1px solid rgba(211, 47, 47, 0.2)',
                          '& .MuiAlert-icon': {
                            color: 'error.main',
                            opacity: 1,
                            mr: 1.5,
                            padding: 0,
                          },
                          '& .MuiAlert-message': {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: 'error.main',
                            padding: 0,
                          },
                          '& .MuiAlert-action': {
                            padding: 0,
                            marginRight: 0,
                          },
                        }}
                        onClose={() => setRegisterError('')}
                      >
                        {registerError}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={isAuthenticating}
                      sx={{
                        textTransform: 'uppercase',
                        marginTop: '10px',
                      }}
                    >
                      {isAuthenticating ? (
                        <CircularProgress size={24} />
                      ) : (
                        'Sign Up'
                      )}
                    </Button>
                  </StyledFormBox>
                  <Box sx={{ mt: 2, mb: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      onClick={() => window.location.href = API_URL + '/api/auth/google'}
                      sx={{
                        backgroundColor: '#fff',
                        color: 'rgba(0,0,0,0.87)',
                        borderColor: '#dadce0',
                        boxShadow: '0 1px 2px rgba(60,64,67,.08)',
                        '&:hover': {
                          backgroundColor: '#f7f8fa',
                          borderColor: '#dadce0',
                        },
                        fontWeight: 500,
                        textTransform: 'none',
                        fontSize: '1rem',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24">
                          <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      Continue with Google
                      </Box>
                    </Button>
                  </Box>
                </FormFieldsContainer>

                {isMobile && (
                  <MobileNavigation>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Already have an account?
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleSignInClick}
                      sx={{
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Sign In
                    </Link>
                  </MobileNavigation>
                )}
              </FormInnerContent>
            </FormContent>
          </SignUpContainer>

          {/* Sign In Container */}
          <SignInContainer
            isRightPanelActive={isRightPanelActive}
            isMobile={isMobile}
          >
            <FormContent>
              <FormInnerContent>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{ fontWeight: 300, mb: 2 }}
                  color="text.primary"
                >
                  Sign In
                </Typography>

                <FormFieldsContainer>
                  <StyledFormBox
                    component="form"
                    noValidate
                    onSubmit={loginFormik.handleSubmit}
                    sx={{ width: '100%' }}
                  >
                    <TextField
                      required
                      fullWidth
                      id="email"
                      label="Email"
                      name="email"
                      placeholder="Enter your email"
                      autoComplete="email"
                      autoFocus
                      value={loginFormik.values.email}
                      onChange={loginFormik.handleChange}
                      onBlur={loginFormik.handleBlur}
                      error={
                        loginFormik.touched.email &&
                        Boolean(loginFormik.errors.email)
                      }
                      helperText={
                        loginFormik.touched.email &&
                        loginFormik.errors.email
                      }
                    />
                    <TextField
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type={showLoginPassword ? 'text' : 'password'}
                      id="password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      value={loginFormik.values.password}
                      onChange={loginFormik.handleChange}
                      onBlur={loginFormik.handleBlur}
                      error={
                        loginFormik.touched.password &&
                        Boolean(loginFormik.errors.password)
                      }
                      helperText={
                        loginFormik.touched.password &&
                        loginFormik.errors.password
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleToggleLoginPassword}
                              edge="end"
                            >
                              {showLoginPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {loginError && (
                      <Alert
                        severity="error"
                        sx={{
                          mt: 1,
                          mb: 1,
                          width: '100%',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 16px',
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          border: '1px solid rgba(211, 47, 47, 0.2)',
                          '& .MuiAlert-icon': {
                            color: 'error.main',
                            opacity: 1,
                            mr: 1.5,
                            padding: 0,
                          },
                          '& .MuiAlert-message': {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: 'error.main',
                            padding: 0,
                          },
                          '& .MuiAlert-action': {
                            padding: 0,
                            marginRight: 0,
                          },
                        }}
                        onClose={() => setLoginError('')}
                      >
                        {loginError}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={isAuthenticating}
                      sx={{
                        textTransform: 'uppercase',
                        marginTop: '10px',
                      }}
                    >
                      {isAuthenticating ? (
                        <CircularProgress size={24} />
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </StyledFormBox>
                  <Box sx={{ mt: 2, mb: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      onClick={() => window.location.href = API_URL + '/api/auth/google'}
                      sx={{
                        backgroundColor: '#fff',
                        color: 'rgba(0,0,0,0.87)',
                        borderColor: '#dadce0',
                        boxShadow: '0 1px 2px rgba(60,64,67,.08)',
                        '&:hover': {
                          backgroundColor: '#f7f8fa',
                          borderColor: '#dadce0',
                        },
                        fontWeight: 500,
                        textTransform: 'none',
                        fontSize: '1rem',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24">
                          <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      Continue with Google
                      </Box>
                    </Button>
                  </Box>
                  <Box textAlign="center">
                    <Link
                      href="#"
                      variant="body2"
                      onClick={handleForgotPasswordClick}
                      sx={{
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>
                </FormFieldsContainer>

                {isMobile && (
                  <MobileNavigation>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Don't have an account?
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleSignUpClick}
                      sx={{
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Sign Up
                    </Link>
                  </MobileNavigation>
                )}
              </FormInnerContent>
            </FormContent>
          </SignInContainer>

          {/* Overlay Container - Only shown on desktop */}
          {!isMobile && (
            <OverlayContainer isRightPanelActive={isRightPanelActive}>
              <Overlay isRightPanelActive={isRightPanelActive}>
                <LeftOverlayPanel isRightPanelActive={isRightPanelActive}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 4,
                      color: 'white',
                      textShadow: isDarkMode
                        ? '1px 1px 3px rgba(0,0,0,0.5)'
                        : 'none',
                    }}
                  >
                    Already have an account?
                  </Typography>
                  <OverlayButton
                    fullWidth
                    backgroundColor="secondary"
                    onClick={handleSignInClick}
                  >
                    Sign In
                  </OverlayButton>
                </LeftOverlayPanel>

                <RightOverlayPanel isRightPanelActive={isRightPanelActive}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 4,
                      color: 'white',
                      textShadow: isDarkMode
                        ? '1px 1px 3px rgba(0,0,0,0.5)'
                        : 'none',
                    }}
                  >
                    Don't have an account?
                  </Typography>
                  <OverlayButton
                    fullWidth
                    backgroundColor="primary"
                    onClick={handleSignUpClick}
                  >
                    Sign Up
                  </OverlayButton>
                </RightOverlayPanel>
              </Overlay>
            </OverlayContainer>
          )}
        </AuthContainer>
      </Container>

      <Dialog open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset your password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Enter your account email and we'll send you a link to set a new password.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email"
            placeholder="Enter your email"
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setForgotPasswordOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" disabled={isSendingReset} onClick={handleSendResetLink}>
            {isSendingReset ? <CircularProgress size={20} /> : 'Send link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Auth;
